import { useNavigate, useParams } from 'react-router-dom';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import {
    MESSAGES,
    ORDER_BY_NUMBER_QUERY_PARAM,
    ORDER_BY_STRING_QUERY_PARAM,
    PAGE_SIZE,
} from 'src/modules/common/constants.ts';
import { Suspense, useCallback, useContext, useDeferredValue, useEffect, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import {
    getValvesPageConfig,
    ValveProduct,
    ValvesPageData,
    ValvesPageVariables,
} from 'src/modules/valves/ValvesPageMapping.ts';
import { skipToken } from '@apollo/client';
import { getFilterComposed } from 'src/modules/common/hooks/getFilterComposed.ts';
import { PATHS } from 'src/router.tsx';
import { useUserInputState } from 'src/modules/user-input/useUserInputState.ts';
import { Device } from 'src/common/Device/Device.tsx';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { composeValveVariables } from 'src/modules/valves/composeValveVariables.ts';
import { getTargetUnits } from 'src/modules/user-input/getTargetUnits.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer';
import { MainLayout } from 'src/modules/common/layouts/MainLayout';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton';
import { BreadcrumbContainer } from 'src/common/Breadcrumb/BreadcrumbContainer';
import { Breadcrumbs } from 'src/modules/common/types/Breadcrumbs';
import { useOrderBy } from 'src/modules/common/hooks/useOrderBy.ts';
import { DirectionEnum, ValvesFilterPageQueryVariables } from 'src/generated/operations.ts';
import { usePageChange } from 'src/modules/common/hooks/usePageChange.ts';
import { generateOrderItems } from 'src/utils/generateOrderItems.ts';
import { SkeletonPageLayout } from 'src/modules/common/skeletons/SkeletonPageLayout/SkeletonPageLayout';
import { Query } from 'src/modules/common/components/Query/Query';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { DeviceGrid, OrderItem } from 'src/common/DeviceGrid/DeviceGrid';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { RecommendedChip } from 'src/modules/valves/RecommendedChip';
import { prepareValveAttributes } from 'src/modules/valves/ValveCard/prepareValveAttributes';
import clsx from 'clsx';
import { FilterSidebarComponent } from 'src/common/FilterSidebarComponent/FilterSidebarComponent';
import { getActuatorsPageConfig } from 'src/modules/actuators/ActuatorsPageMapping.ts';
import { FilterEventDetails } from 'src/modules/common/types/FilterEventDetails.ts';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler.ts';

const limit = PAGE_SIZE;
export const ValvesPage = () => {
    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);
    const pageConfig = useMemo(() => getValvesPageConfig(userSettings.currentRegion), [userSettings]);
    const ActuatorsPageConfig = useMemo(() => getActuatorsPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null || ActuatorsPageConfig === null) {
        throw new Error('No Page Config!');
    }
    const [userInput, , , userInputPageValue,, locationState, setLocationState] = useUserInputState(
        ActuatorsPageConfig.filterConfig,
    );

    const [
        searchParams,
        filter,,,,
        setSearchParams,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.filterConfig,
    ]);

    const [
        ,
        filterWithoutMainFilterConfig,
        checkboxHandler,,
        filtersReset,
    ] = useFilterLogic([
        ...pageConfig.filterConfig,
    ]);

    const navigate = useNavigate();

    const [homePageValue, hasAdditionalFilter] = useCheckMainFilter({
        searchParams,
        filter,
        navigate,
        filterConfig: pageConfig.filterConfig,
    });

    const { page } = useParams();

    const currentPage = useMemo(() => {
        const parsedPage = parseInt(page || '1', 10);
        return parsedPage < 1 ? 1 : parsedPage;
    }, [page]);

    useEffect(() => {
        if (currentPage.toString() !== locationState.valvesPage) {
            setLocationState({
                ...locationState,
                valvesPage: currentPage.toString(),
            });
        }
    }, [currentPage, locationState, setLocationState]);

    const [orderByNumber, setOrderByNumber] = useOrderBy(ORDER_BY_NUMBER_QUERY_PARAM);

    const variables = useMemo((): { variables: ValvesPageVariables } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        const [filterMain] = getFilterComposed([
            ...pageConfig.mainFilterConfig,
            ...pageConfig.filterConfig,
        ], filter, mandatoryFilterConfig, userSettings.currentLocale);

        const offset = (currentPage - 1) * limit;

        return {
            variables: {
                filter: filterMain,
                ...composeValveVariables(userInput, getTargetUnits(userSettings.currentRegion)),
                locale: userSettings.currentLocale,
                offset,
                limit,
                orderByNumber,
            },
        };
    }, [
        orderByNumber,
        filter,
        userSettings,
        currentPage,
        mandatoryFilterConfig,
        pageConfig,
        userInput,
    ]);

    const defVariables = useDeferredValue(variables);

    const isStale = defVariables !== variables;

    const filtersVariables = useMemo((): typeof skipToken | {
        variables: Omit<ValvesFilterPageQueryVariables, 'id'>;
    } => {
        if (filter === null) {
            return skipToken;
        }
        const [filterMain] = getFilterComposed([
            ...pageConfig.mainFilterConfig,
            ...pageConfig.filterConfig,
        ], filter, mandatoryFilterConfig, userSettings.currentLocale);

        return {
            variables: {
                filter: filterMain,
                locale: userSettings.currentLocale,
                region: userSettings.currentRegion,
                ...composeValveVariables(userInput, getTargetUnits(userSettings.currentRegion)),
            },
        };
    }, [
        userInput,
        filter,
        userSettings,
        mandatoryFilterConfig,
        pageConfig,
    ]);

    const updatedLocation = useCallback((newPage: number) => {
        setLocationState({
            ...locationState,
            valveLabel: '',
            valvesPage: newPage.toFixed(),
            actuatorLabel: '-',
            actuatorsPage: '1',
            selectedAccessories: [],
        });
    }, [setLocationState, locationState]);

    const onPageChange = usePageChange(
        (newPage: number) => `${PATHS.Valves}/${newPage}`,
        updatedLocation,
    );

    const valvesCheckboxHandler: CheckboxHandler = useCallback((detail: FilterEventDetails) => {
        onPageChange(1, searchParams);
        return checkboxHandler(detail);
    }, [onPageChange, checkboxHandler, searchParams]);

    const resetValveFilters = useMemo(() => () => {
        onPageChange(1, searchParams);
        filtersReset(pageConfig.filterConfig);
    }, [pageConfig, filtersReset, searchParams, onPageChange]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="back"
                to={{
                    pathname: PATHS.UserInput,
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.back}/>
            </NavigationButton>
            <NavigationButton
                type="forward"
                eventData={{
                    event: 'gaEvent',
                    eventCategory: 'sizing tool',
                    eventAction: 'step 3 - valve selection',
                    eventLabel: 'select this valve',
                }}
                disabled={locationState.valveLabel.length < 2}
                to={{
                    pathname: [
                        ...(locationState.valveLabel === locationState.actuatorLabel ? [
                            PATHS.Accessories,
                            encodeURIComponent(locationState.valveLabel),
                            encodeURIComponent(locationState.actuatorLabel),
                            currentPage.toString(),
                        ] : [
                            PATHS.Actuators,
                            encodeURIComponent(locationState.valveLabel),
                            locationState.actuatorsPage,
                        ]),
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const breadcrumbProps: Breadcrumbs = [
        {
            title: MESSAGES.selectValveType,
            value: homePageValue,
            to: {
                pathname: PATHS.Home,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.RequestedFlow,
            value: userInputPageValue,
            to: {
                pathname: PATHS.UserInput,
                search: searchParams.toString(),
            },
        },
        {
            selected: true,
            title: MESSAGES.SelectedValve,
            disabled: true,
            value: locationState.valveLabel,
            to: '#',
            actionShown: locationState.valveLabel.length > 2,
            onActionClick: () => {
                setLocationState({
                    ...locationState,
                    valveLabel: '-',
                    actuatorLabel: '-',
                    actuatorsPage: '1',
                    selectedAccessories: [],
                });
            },
        },
        {
            title: MESSAGES.SelectedActuator,
            disabled: true,
            value: locationState.actuatorLabel,
            to: '#',
        },
        {
            title: MESSAGES.SelectedAccessories,
            disabled: true,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.join(', ') : '-',
            to: '#',
        },
    ];

    return (
        <>
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.valvesPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={<BreadcrumbContainer isMobile={isMobile} breadcrumbs={breadcrumbProps}/>}
                />

                <Suspense fallback={<SkeletonPageLayout/>}>
                    <Query<ValvesPageData, ValvesPageVariables>
                        query={pageConfig.query}
                        variables={defVariables}
                        onResult={(data) => {
                            if (data &&
                            data.list.count === 0 &&
                            (userInput.v100.length > 0 || userInput.kvs.length > 0)
                            ) {
                                navigate({
                                    pathname: PATHS.UserInput,
                                    search: searchParams.toString(),
                                }, {
                                    state: {
                                        noResults: true,
                                    },
                                });
                            }
                        }}
                    >
                        {({ data }) => {
                            if (data === undefined) {
                                return (
                                    <SkeletonPageLayout/>
                                );
                            }

                            if (data.list.products.length === 0) {
                                return (
                                    <div className={clsx({
                                        'vss-valves-page': true,
                                        'vss-valves-page--mobile': isMobile,
                                    })}
                                    >
                                        <FilterSidebarComponent
                                            filterConfigSecondary={pageConfig.filterConfig}
                                            genericFilterQuery={pageConfig.genericFilterQuery}
                                            variables={filtersVariables}
                                            checkboxHandler={valvesCheckboxHandler}
                                            handleReset={resetValveFilters}
                                            filter={filterWithoutMainFilterConfig}
                                        />

                                        <NoData/>
                                    </div>
                                );
                            }

                            const [firstItem] = data.list.products;
                            const firstProduct = firstItem.product;

                            const defaultOrderLabel = userInput.kvs.length > 0 || userInput.v100.length > 0
                                ? MESSAGES.recommended : MESSAGES.Label;

                            const isUserInputProvided = userInput.v100.length > 0 ||
                            userInput.kvs.length > 0;

                            let orderItems: OrderItem[] = [];
                            if ('cv' in firstProduct) {
                                orderItems = generateOrderItems(
                                    [
                                        'bodySize',
                                        'cv',
                                        'vnomgpm',
                                        'cvsequence1',
                                        'CloseOffPressure',
                                    ].filter((name) => !!firstProduct[name as keyof ValveProduct]),
                                    pageConfig?.orderItemsConfig,
                                    DirectionEnum.Asc,
                                );
                                if (isUserInputProvided) {
                                    const calcLabels = [
                                        MESSAGES.FlowVelocity,
                                        MESSAGES.EffectiveDpv,
                                    ];
                                    generateOrderItems(
                                        [
                                            'bodySize',
                                            'cv',
                                        ].filter((name) => !!firstProduct[name as keyof ValveProduct]),
                                        pageConfig?.orderItemsConfig,
                                        DirectionEnum.Desc,
                                    ).forEach(([, id, direction], index) => {
                                        const label = calcLabels[index];
                                        if (label !== undefined) {
                                            orderItems.push([label, id, direction]);
                                        }
                                    });
                                }
                            }

                            if ('kvs' in firstProduct) {
                                orderItems = generateOrderItems(
                                    [
                                        'dn',
                                        'kvs',
                                        'kvmax',
                                        'vnomls',
                                        'vnomlh',
                                        'kvssequence1',
                                        'PN',
                                    ].filter((name) => !!firstProduct[name as keyof ValveProduct]),
                                    pageConfig?.orderItemsConfig,
                                    DirectionEnum.Asc,
                                );
                                if (isUserInputProvided) {
                                    const calcLabels = [
                                        MESSAGES.FlowVelocity,
                                        MESSAGES.EffectiveDpv,
                                        MESSAGES.EffectivePV,
                                        MESSAGES.MinReqDpv,
                                    ];
                                    generateOrderItems(
                                        [
                                            'dn',
                                            'kvs',
                                            'kvs',
                                            'kvstheor',
                                        ].filter((name) => !!firstProduct[name as keyof ValveProduct]),
                                        pageConfig?.orderItemsConfig,
                                        DirectionEnum.Desc,
                                    ).forEach(([, id, direction], index) => {
                                        const label = calcLabels[index];
                                        if (label !== undefined) {
                                            orderItems.push([label, id, direction]);
                                        }
                                    });
                                }
                            }

                            return (
                                <div className={clsx({
                                    'vss-valves-page': true,
                                    'vss-valves-page--mobile': isMobile,
                                })}
                                >
                                    <FilterSidebarComponent
                                        filterConfigSecondary={pageConfig.filterConfig}
                                        genericFilterQuery={pageConfig.genericFilterQuery}
                                        variables={filtersVariables}
                                        checkboxHandler={valvesCheckboxHandler}
                                        handleReset={resetValveFilters}
                                        filter={filterWithoutMainFilterConfig}
                                    />

                                    <div className={clsx({
                                        'vss-valves-page__list': true,
                                        'vss-valves-page__list--stale': isStale,
                                    })}
                                    >
                                        <DeviceGrid
                                            isMobile={isMobile}
                                            hasAdditionalFilter={hasAdditionalFilter}
                                            currentPage={currentPage}
                                            pageSize={limit}
                                            total={data.list.count || 0}
                                            onPageChange={(p: number) => onPageChange(p, searchParams)}
                                            defaultOrderLabel={defaultOrderLabel}
                                            orderItems={orderItems}
                                            orderSelected={[...orderByNumber]}
                                            onOrderByChange={(newOrder) => {
                                                onPageChange(1, setOrderByNumber(searchParams, newOrder));
                                            }}
                                        >
                                            {data?.list.products.map(({ product, rank }) => (
                                                <Device
                                                    key={`${product.ID}_${product.Label}`}
                                                    defaultImage={<ValveIcon/>}
                                                    chip={<RecommendedChip rank={rank}/>}
                                                    isSelected={locationState.valveLabel === product.Label}
                                                    product={product}
                                                    attributes={prepareValveAttributes(product)}
                                                    onDeviceClick={({ label }) => {
                                                        const isActuatorUnit = product?.valveType?.raw.join('')
                                                            .toLowerCase() === 'valve-actuator unit';
                                                        setLocationState({
                                                            ...locationState,
                                                            valveLabel: label,
                                                            actuatorLabel: isActuatorUnit ? label : '-',
                                                            actuatorsPage: isActuatorUnit
                                                                ? currentPage.toString()
                                                                : '1',
                                                            selectedAccessories: [],
                                                        });
                                                        setSearchParams((search) => {
                                                            ActuatorsPageConfig.filterConfig.forEach(([name]) => {
                                                                search.delete(name);
                                                            });
                                                            // reset custom sorting for actuators list
                                                            search.delete(`${ORDER_BY_NUMBER_QUERY_PARAM}_a`);
                                                            search.delete(`${ORDER_BY_STRING_QUERY_PARAM}_a`);
                                                            return search;
                                                        });
                                                    }}
                                                />
                                            ))}
                                        </DeviceGrid>
                                    </div>
                                </div>
                            );
                        }}
                    </Query>
                </Suspense>
            </MainLayout>

            {isMobile && navigation}
        </>
    );
};
